import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const PHONE_CHANGE_LABELS = {
  en: {
    step1Label: 'STEP 1 OF 2 — VERIFY CURRENT NUMBER',
    step1Hint: 'OTP will be sent to your registered mobile number',
    step2Label: 'STEP 2 OF 2 — NEW NUMBER',
    step2Hint: 'Enter your new 10-digit mobile number',
    sendOtp: 'Send OTP',
    sending: 'Sending…',
    enterOtpPlaceholder: 'Enter OTP',
    verifying: 'Verifying…',
    verify: 'Verify',
    newPhonePlaceholder: 'New mobile number',
    sendOtpNew: 'Send OTP to new number',
    newOtpPlaceholder: 'OTP from new number',
    reasonPlaceholder: 'Reason (optional)',
    submitting: 'Submitting…',
    verifyNew: 'Verify new number',
    successLine1: 'Your request has been submitted. Two admins must approve before your number is updated.',
    successLine2: 'You will receive WhatsApp, SMS and email alert once approved.',
    close: 'Close',
    pendingRequestError: 'You already have a pending request. Please wait for admin approval.',
    couldNotSendOtp: 'Could not send OTP',
    enterSixDigitOtp: 'Enter the 6-digit OTP',
    verificationFailed: 'Verification failed',
    invalidMobile: 'Enter a valid 10-digit mobile number',
  },
  mr: {
    step1Label: 'पायरी १ पैकी २ — सध्याचा नंबर पडताळा',
    step1Hint: 'नोंदणीकृत मोबाईल नंबरवर OTP पाठवला जाईल',
    step2Label: 'पायरी २ पैकी २ — नवीन नंबर',
    step2Hint: 'तुमचा नवीन १० अंकी मोबाईल नंबर टाका',
    sendOtp: 'OTP पाठवा',
    sending: 'पाठवत आहे…',
    enterOtpPlaceholder: 'OTP टाका',
    verifying: 'पडताळत आहे…',
    verify: 'पडताळा',
    newPhonePlaceholder: 'नवीन मोबाईल नंबर',
    sendOtpNew: 'नवीन नंबरवर OTP पाठवा',
    newOtpPlaceholder: 'नवीन नंबरवरून OTP',
    reasonPlaceholder: 'कारण (ऐच्छिक)',
    submitting: 'सबमिट करत आहे…',
    verifyNew: 'नवीन नंबर पडताळा',
    successLine1: 'तुमची विनंती सबमिट झाली. नंबर अपडेट होण्यापूर्वी दोन अॅडमिननी मंजुरी द्यावी लागेल.',
    successLine2: 'मंजुरीनंतर WhatsApp, SMS आणि email सूचना मिळेल.',
    close: 'बंद करा',
    pendingRequestError: 'आधीच प्रलंबित विनंती आहे. अॅडमिन मंजुरीची प्रतीक्षा करा.',
    couldNotSendOtp: 'OTP पाठवता आला नाही',
    enterSixDigitOtp: '६ अंकी OTP टाका',
    verificationFailed: 'पडताळणी अयशस्वी',
    invalidMobile: 'वैध १० अंकी मोबाईल नंबर टाका',
  },
  hi: {
    step1Label: 'चरण १ में २ — वर्तमान नंबर सत्यापित करें',
    step1Hint: 'आपके पंजीकृत मोबाइल नंबर पर OTP भेजा जाएगा',
    step2Label: 'चरण २ में २ — नया नंबर',
    step2Hint: 'अपना नया १० अंकीय मोबाइल नंबर दर्ज करें',
    sendOtp: 'OTP भेजें',
    sending: 'भेजा जा रहा है…',
    enterOtpPlaceholder: 'OTP दर्ज करें',
    verifying: 'सत्यापित हो रहा है…',
    verify: 'सत्यापित करें',
    newPhonePlaceholder: 'नया मोबाइल नंबर',
    sendOtpNew: 'नए नंबर पर OTP भेजें',
    newOtpPlaceholder: 'नए नंबर से OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा किया जा रहा है…',
    verifyNew: 'नया नंबर सत्यापित करें',
    successLine1: 'आपका अनुरोध जमा हो गया है। नंबर अपडेट होने से पहले दो एडमिन की स्वीकृति आवश्यक है।',
    successLine2: 'स्वीकृति के बाद आपको WhatsApp, SMS और email सूचना मिलेगी।',
    close: 'बंद करें',
    pendingRequestError: 'आपका एक अनुरोध पहले से लंबित है। कृपया एडमिन स्वीकृति की प्रतीक्षा करें।',
    couldNotSendOtp: 'OTP भेजा नहीं जा सका',
    enterSixDigitOtp: '६ अंकीय OTP दर्ज करें',
    verificationFailed: 'सत्यापन विफल',
    invalidMobile: 'वैध १० अंकीय मोबाइल नंबर दर्ज करें',
  },
  gu: {
    step1Label: 'પગલું ૧ માંથી ૨ — વર્તમાન નંબર ચકાસો',
    step1Hint: 'તમારા નોંદાયેલ મોબાઇલ નંબર પર OTP મોકલવામાં આવશે',
    step2Label: 'પગલું ૨ માંથી ૨ — નવો નંબર',
    step2Hint: 'તમારો નવો ૧૦ અંકનો મોબાઇલ નંબર દાખલ કરો',
    sendOtp: 'OTP મોકલો',
    sending: 'મોકલી રહ્યા છીએ…',
    enterOtpPlaceholder: 'OTP દાખલ કરો',
    verifying: 'ચકાસણી થઈ રહી છે…',
    verify: 'ચકાસો',
    newPhonePlaceholder: 'નવો મોબાઇલ નંબર',
    sendOtpNew: 'નવા નંબર પર OTP મોકલો',
    newOtpPlaceholder: 'નવા નંબરથી OTP',
    reasonPlaceholder: 'કારણ (વૈકલ્પિક)',
    submitting: 'સબમિટ થઈ રહ્યું છે…',
    verifyNew: 'નવો નંબર ચકાસો',
    successLine1: 'તમારી વિનંતી સબમિટ થઈ ગઈ છે. નંબર અપડેટ થાય તે પહેલાં બે એડમિનની મંજૂરી જરૂરી છે.',
    successLine2: 'મંજૂરી પછી તમને WhatsApp, SMS અને email સૂચના મળશે.',
    close: 'બંધ કરો',
    pendingRequestError: 'તમારી એક વિનંતી પહેલેથી બાકી છે. કૃપા કરીને એડમિન મંજૂરીની રાહ જુઓ.',
    couldNotSendOtp: 'OTP મોકલી શકાયો નહીં',
    enterSixDigitOtp: '૬ અંકનો OTP દાખલ કરો',
    verificationFailed: 'ચકાસણી નિષ્ફળ',
    invalidMobile: 'માન્ય ૧૦ અંકનો મોબાઇલ નંબર દાખલ કરો',
  },
  ta: {
    step1Label: 'படி 1 இல் 2 — தற்போதைய எண்ணை சரிபார்க்கவும்',
    step1Hint: 'உங்கள் பதிவு செய்த மொபைல் எண்ணுக்கு OTP அனுப்பப்படும்',
    step2Label: 'படி 2 இல் 2 — புதிய எண்',
    step2Hint: 'உங்கள் புதிய 10 இலக்க மொபைல் எண்ணை உள்ளிடவும்',
    sendOtp: 'OTP அனுப்பு',
    sending: 'அனுப்புகிறது…',
    enterOtpPlaceholder: 'OTP உள்ளிடவும்',
    verifying: 'சரிபார்க்கிறது…',
    verify: 'சரிபார்',
    newPhonePlaceholder: 'புதிய மொபைல் எண்',
    sendOtpNew: 'புதிய எண்ணுக்கு OTP அனுப்பு',
    newOtpPlaceholder: 'புதிய எண்ணிலிருந்து OTP',
    reasonPlaceholder: 'காரணம் (விரும்பினால்)',
    submitting: 'சமர்ப்பிக்கிறது…',
    verifyNew: 'புதிய எண்ணை சரிபார்',
    successLine1: 'உங்கள் கோரிக்கை சமர்ப்பிக்கப்பட்டது. எண் புதுப்பிக்கப்படுவதற்கு முன் இரண்டு நிர்வாகிகளின் அனுமதி தேவை.',
    successLine2: 'அனுமதிக்கப்பட்ட பிறகு WhatsApp, SMS மற்றும் email அறிவிப்பு கிடைக்கும்.',
    close: 'மூடு',
    pendingRequestError: 'உங்களிடம் ஏற்கனவே நிலுவையில் உள்ள கோரிக்கை உள்ளது. நிர்வாகி அனுமதிக்க காத்திருக்கவும்.',
    couldNotSendOtp: 'OTP அனுப்ப முடியவில்லை',
    enterSixDigitOtp: '6 இலக்க OTP உள்ளிடவும்',
    verificationFailed: 'சரிபார்ப்பு தோல்வி',
    invalidMobile: 'செல்லுபடியாகும் 10 இலக்க மொபைல் எண்ணை உள்ளிடவும்',
  },
  te: {
    step1Label: 'దశ 1 లో 2 — ప్రస్తుత నంబర్ ధృవీకరించండి',
    step1Hint: 'మీ నమోదైన మొబైల్ నంబర్‌కు OTP పంపబడుతుంది',
    step2Label: 'దశ 2 లో 2 — కొత్త నంబర్',
    step2Hint: 'మీ కొత్త 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి',
    sendOtp: 'OTP పంపండి',
    sending: 'పంపుతోంది…',
    enterOtpPlaceholder: 'OTP నమోదు చేయండి',
    verifying: 'ధృవీకరిస్తోంది…',
    verify: 'ధృవీకరించండి',
    newPhonePlaceholder: 'కొత్త మొబైల్ నంబర్',
    sendOtpNew: 'కొత్త నంబర్‌కు OTP పంపండి',
    newOtpPlaceholder: 'కొత్త నంబర్ నుండి OTP',
    reasonPlaceholder: 'కారణం (ఐచ్ఛికం)',
    submitting: 'సమర్పిస్తోంది…',
    verifyNew: 'కొత్త నంబర్ ధృవీకరించండి',
    successLine1: 'మీ అభ్యర్థన సమర్పించబడింది. నంబర్ అప్‌డేట్ కావడానికి ముందు ఇద్దరు అడ్మిన్‌ల ఆమోదం అవసరం.',
    successLine2: 'ఆమోదం తర్వాత WhatsApp, SMS మరియు email అలర్ట్ వస్తుంది.',
    close: 'మూసివేయి',
    pendingRequestError: 'మీకు ఇప్పటికే పెండింగ్ అభ్యర్థన ఉంది. అడ్మిన్ ఆమోదం కోసం వేచి ఉండండి.',
    couldNotSendOtp: 'OTP పంపలేకపోయాము',
    enterSixDigitOtp: '6 అంకెల OTP నమోదు చేయండి',
    verificationFailed: 'ధృవీకరణ విఫలమైంది',
    invalidMobile: 'చెల్లుబాటు అయ్యే 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి',
  },
  kn: {
    step1Label: 'ಹಂತ 1 ರಲ್ಲಿ 2 — ಪ್ರಸ್ತುತ ಸಂಖ್ಯೆಯನ್ನು ಪರಿಶೀಲಿಸಿ',
    step1Hint: 'ನಿಮ್ಮ ನೋಂದಾಯಿತ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಗೆ OTP ಕಳುಹಿಸಲಾಗುತ್ತದೆ',
    step2Label: 'ಹಂತ 2 ರಲ್ಲಿ 2 — ಹೊಸ ಸಂಖ್ಯೆ',
    step2Hint: 'ನಿಮ್ಮ ಹೊಸ 10 ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ',
    sendOtp: 'OTP ಕಳುಹಿಸಿ',
    sending: 'ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…',
    enterOtpPlaceholder: 'OTP ನಮೂದಿಸಿ',
    verifying: 'ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ…',
    verify: 'ಪರಿಶೀಲಿಸಿ',
    newPhonePlaceholder: 'ಹೊಸ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
    sendOtpNew: 'ಹೊಸ ಸಂಖ್ಯೆಗೆ OTP ಕಳುಹಿಸಿ',
    newOtpPlaceholder: 'ಹೊಸ ಸಂಖ್ಯೆಯಿಂದ OTP',
    reasonPlaceholder: 'ಕಾರಣ (ಐಚ್ಛಿಕ)',
    submitting: 'ಸಲ್ಲಿಸಲಾಗುತ್ತಿದೆ…',
    verifyNew: 'ಹೊಸ ಸಂಖ್ಯೆಯನ್ನು ಪರಿಶೀಲಿಸಿ',
    successLine1: 'ನಿಮ್ಮ ವಿನಂತಿಯನ್ನು ಸಲ್ಲಿಸಲಾಗಿದೆ. ಸಂಖ್ಯೆ ನವೀಕರಿಸುವ ಮೊದಲು ಇಬ್ಬರು ಅಡ್ಮಿನ್‌ಗಳ ಅನುಮೋದನೆ ಅಗತ್ಯ.',
    successLine2: 'ಅನುಮೋದನೆಯ ನಂತರ WhatsApp, SMS ಮತ್ತು email ಎಚ್ಚರಿಕೆ ಸಿಗುತ್ತದೆ.',
    close: 'ಮುಚ್ಚಿ',
    pendingRequestError: 'ನಿಮ್ಮ ಬಾಕಿ ವಿನಂತಿ ಈಗಾಗಲೇ ಇದೆ. ಅಡ್ಮಿನ್ ಅನುಮೋದನೆಗಾಗಿ ಕಾಯಿರಿ.',
    couldNotSendOtp: 'OTP ಕಳುಹಿಸಲಾಗಲಿಲ್ಲ',
    enterSixDigitOtp: '6 ಅಂಕಿಯ OTP ನಮೂದಿಸಿ',
    verificationFailed: 'ಪರಿಶೀಲನೆ ವಿಫಲವಾಗಿದೆ',
    invalidMobile: 'ಮಾನ್ಯ 10 ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ',
  },
  ml: {
    step1Label: 'ഘട്ടം 1 / 2 — നിലവിലെ നമ്പർ സ്ഥിരീകരിക്കുക',
    step1Hint: 'നിങ്ങളുടെ രജിസ്റ്റർ ചെയ്ത മൊബൈൽ നമ്പറിലേക്ക് OTP അയയ്ക്കും',
    step2Label: 'ഘട്ടം 2 / 2 — പുതിയ നമ്പർ',
    step2Hint: 'നിങ്ങളുടെ പുതിയ 10 അക്ക മൊബൈൽ നമ്പർ നൽകുക',
    sendOtp: 'OTP അയയ്ക്കുക',
    sending: 'അയയ്ക്കുന്നു…',
    enterOtpPlaceholder: 'OTP നൽകുക',
    verifying: 'സ്ഥിരീകരിക്കുന്നു…',
    verify: 'സ്ഥിരീകരിക്കുക',
    newPhonePlaceholder: 'പുതിയ മൊബൈൽ നമ്പർ',
    sendOtpNew: 'പുതിയ നമ്പറിലേക്ക് OTP അയയ്ക്കുക',
    newOtpPlaceholder: 'പുതിയ നമ്പരിൽ നിന്നുള്ള OTP',
    reasonPlaceholder: 'കാരണം (ഓപ്ഷണൽ)',
    submitting: 'സമർപ്പിക്കുന്നു…',
    verifyNew: 'പുതിയ നമ്പർ സ്ഥിരീകരിക്കുക',
    successLine1: 'നിങ്ങളുടെ അഭ്യർത്ഥന സമർപ്പിച്ചു. നമ്പർ അപ്‌ഡേറ്റ് ചെയ്യുന്നതിന് മുമ്പ് രണ്ട് അഡ്മിൻ അംഗീകാരം ആവശ്യമാണ്.',
    successLine2: 'അംഗീകരിച്ചതിന് ശേഷം WhatsApp, SMS, email അലേർട്ട് ലഭിക്കും.',
    close: 'അടയ്ക്കുക',
    pendingRequestError: 'നിങ്ങൾക്ക് ഇതിനകം തീർപ്പാക്കാത്ത അഭ്യർത്ഥന ഉണ്ട്. അഡ്മിൻ അംഗീകാരത്തിനായി കാത്തിരിക്കുക.',
    couldNotSendOtp: 'OTP അയയ്ക്കാനായില്ല',
    enterSixDigitOtp: '6 അക്ക OTP നൽകുക',
    verificationFailed: 'സ്ഥിരീകരണം പരാജയപ്പെട്ടു',
    invalidMobile: 'സാധുവായ 10 അക്ക മൊബൈൽ നമ്പർ നൽകുക',
  },
  pa: {
    step1Label: 'ਕਦਮ ੧ ਵਿੱਚੋਂ ੨ — ਮੌਜੂਦਾ ਨੰਬਰ ਪੁਸ਼ਟੀ ਕਰੋ',
    step1Hint: 'ਤੁਹਾਡੇ ਰਜਿਸਟਰਡ ਮੋਬਾਈਲ ਨੰਬਰ ਤੇ OTP ਭੇਜਿਆ ਜਾਵੇਗਾ',
    step2Label: 'ਕਦਮ ੨ ਵਿੱਚੋਂ ੨ — ਨਵਾਂ ਨੰਬਰ',
    step2Hint: 'ਆਪਣਾ ਨਵਾਂ ੧੦ ਅੰਕਾਂ ਵਾਲਾ ਮੋਬਾਈਲ ਨੰਬਰ ਦਾਖਲ ਕਰੋ',
    sendOtp: 'OTP ਭੇਜੋ',
    sending: 'ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
    enterOtpPlaceholder: 'OTP ਦਾਖਲ ਕਰੋ',
    verifying: 'ਪੁਸ਼ਟੀ ਹੋ ਰਹੀ ਹੈ…',
    verify: 'ਪੁਸ਼ਟੀ ਕਰੋ',
    newPhonePlaceholder: 'ਨਵਾਂ ਮੋਬਾਈਲ ਨੰਬਰ',
    sendOtpNew: 'ਨਵੇਂ ਨੰਬਰ ਤੇ OTP ਭੇਜੋ',
    newOtpPlaceholder: 'ਨਵੇਂ ਨੰਬਰ ਤੋਂ OTP',
    reasonPlaceholder: 'ਕਾਰਨ (ਵਿਕਲਪਿਕ)',
    submitting: 'ਜਮਾ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ…',
    verifyNew: 'ਨਵਾਂ ਨੰਬਰ ਪੁਸ਼ਟੀ ਕਰੋ',
    successLine1: 'ਤੁਹਾਡੀ ਬੇਨਤੀ ਜਮਾ ਹੋ ਗਈ ਹੈ। ਨੰਬਰ ਅਪਡੇਟ ਹੋਣ ਤੋਂ ਪਹਿਲਾਂ ਦੋ ਐਡਮਿਨ ਦੀ ਮਨਜ਼ੂਰੀ ਲੋੜੀਂਦੀ ਹੈ।',
    successLine2: 'ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਤੁਹਾਨੂੰ WhatsApp, SMS ਅਤੇ email ਸੂਚਨਾ ਮਿਲੇਗੀ।',
    close: 'ਬੰਦ ਕਰੋ',
    pendingRequestError: 'ਤੁਹਾਡੀ ਇੱਕ ਬੇਨਤੀ ਪਹਿਲਾਂ ਤੋਂ ਲੰਬਿਤ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਦੀ ਉਡੀਕ ਕਰੋ।',
    couldNotSendOtp: 'OTP ਭੇਜਿਆ ਨਹੀਂ ਜਾ ਸਕਿਆ',
    enterSixDigitOtp: '੬ ਅੰਕਾਂ ਵਾਲਾ OTP ਦਾਖਲ ਕਰੋ',
    verificationFailed: 'ਪੁਸ਼ਟੀ ਅਸਫਲ',
    invalidMobile: 'ਵੈਧ ੧੦ ਅੰਕਾਂ ਵਾਲਾ ਮੋਬਾਈਲ ਨੰਬਰ ਦਾਖਲ ਕਰੋ',
  },
  bn: {
    step1Label: 'ধাপ ১ এর ২ — বর্তমান নম্বর যাচাই করুন',
    step1Hint: 'আপনার নিবন্ধিত মোবাইল নম্বরে OTP পাঠানো হবে',
    step2Label: 'ধাপ ২ এর ২ — নতুন নম্বর',
    step2Hint: 'আপনার নতুন ১০ সংখ্যার মোবাইল নম্বর লিখুন',
    sendOtp: 'OTP পাঠান',
    sending: 'পাঠানো হচ্ছে…',
    enterOtpPlaceholder: 'OTP লিখুন',
    verifying: 'যাচাই হচ্ছে…',
    verify: 'যাচাই করুন',
    newPhonePlaceholder: 'নতুন মোবাইল নম্বর',
    sendOtpNew: 'নতুন নম্বরে OTP পাঠান',
    newOtpPlaceholder: 'নতুন নম্বর থেকে OTP',
    reasonPlaceholder: 'কারণ (ঐচ্ছিক)',
    submitting: 'জমা দেওয়া হচ্ছে…',
    verifyNew: 'নতুন নম্বর যাচাই করুন',
    successLine1: 'আপনার অনুরোধ জমা হয়েছে। নম্বর আপডেট হওয়ার আগে দুই জন অ্যাডমিনের অনুমোদন প্রয়োজন।',
    successLine2: 'অনুমোদনের পর আপনি WhatsApp, SMS এবং email সতর্কতা পাবেন।',
    close: 'বন্ধ করুন',
    pendingRequestError: 'আপনার একটি অনুরোধ ইতিমধ্যে মুলতুবি আছে। অ্যাডমিন অনুমোদনের জন্য অপেক্ষা করুন।',
    couldNotSendOtp: 'OTP পাঠানো যায়নি',
    enterSixDigitOtp: '৬ সংখ্যার OTP লিখুন',
    verificationFailed: 'যাচাই ব্যর্থ',
    invalidMobile: 'বৈধ ১০ সংখ্যার মোবাইল নম্বর লিখুন',
  },
  or: {
    step1Label: 'ପଦ ୧ ରୁ ୨ — ବର୍ତ୍ତମାନ ନମ୍ବର ଯାଞ୍ଚ କରନ୍ତୁ',
    step1Hint: 'ଆପଣଙ୍କ ପଞ୍ଜୀକୃତ ମୋବାଇଲ ନମ୍ବରକୁ OTP ପଠାଯିବ',
    step2Label: 'ପଦ ୨ ରୁ ୨ — ନୂଆ ନମ୍ବର',
    step2Hint: 'ଆପଣଙ୍କ ନୂଆ ୧୦ ଅଙ୍କର ମୋବାଇଲ ନମ୍ବର ଲେଖନ୍ତୁ',
    sendOtp: 'OTP ପଠାନ୍ତୁ',
    sending: 'ପଠାଯାଉଛି…',
    enterOtpPlaceholder: 'OTP ଲେଖନ୍ତୁ',
    verifying: 'ଯାଞ୍ଚ ହେଉଛି…',
    verify: 'ଯାଞ୍ଚ କରନ୍ତୁ',
    newPhonePlaceholder: 'ନୂଆ ମୋବାଇଲ ନମ୍ବର',
    sendOtpNew: 'ନୂଆ ନମ୍ବରକୁ OTP ପଠାନ୍ତୁ',
    newOtpPlaceholder: 'ନୂଆ ନମ୍ବରରୁ OTP',
    reasonPlaceholder: 'କାରଣ (ଐଚ୍ଛିକ)',
    submitting: 'ଜମା ହେଉଛି…',
    verifyNew: 'ନୂଆ ନମ୍ବର ଯାଞ୍ଚ କରନ୍ତୁ',
    successLine1: 'ଆପଣଙ୍କ ଅନୁରୋଧ ଜମା ହୋଇଛି। ନମ୍ବର ଅପଡେଟ୍ ହେବା ପୂର୍ବରୁ ଦୁଇ ଜଣ ଆଡମିନଙ୍କ ଅନୁମୋଦନ ଆବଶ୍ୟକ।',
    successLine2: 'ଅନୁମୋଦନ ପରେ ଆପଣଙ୍କୁ WhatsApp, SMS ଏବଂ email ସଚେତନା ମିଳିବ।',
    close: 'ବନ୍ଦ କରନ୍ତୁ',
    pendingRequestError: 'ଆପଣଙ୍କର ଏକ ଅନୁରୋଧ ପୂର୍ବରୁ ବିଚାରାଧୀନ ଅଛି। ଆଡମିନ ଅନୁମୋଦନ ପାଇଁ ଅପେକ୍ଷା କରନ୍ତୁ।',
    couldNotSendOtp: 'OTP ପଠାଯାଇପାରିଲା ନାହିଁ',
    enterSixDigitOtp: '୬ ଅଙ୍କର OTP ଲେଖନ୍ତୁ',
    verificationFailed: 'ଯାଞ୍ଚ ବିଫଳ',
    invalidMobile: 'ବୈଧ ୧୦ ଅଙ୍କର ମୋବାଇଲ ନମ୍ବର ଲେଖନ୍ତୁ',
  },
  as: {
    step1Label: 'পদক্ষেপ ১ ৰ ২ — বৰ্তমান নম্বৰ পৰীক্ষা কৰক',
    step1Hint: 'আপোনাৰ পঞ্জীভুক্ত মোবাইল নম্বৰলৈ OTP পঠোৱা হ’ব',
    step2Label: 'পদক্ষেপ ২ ৰ ২ — নতুন নম্বৰ',
    step2Hint: 'আপোনাৰ নতুন ১০ অংকৰ মোবাইল নম্বৰ লিখক',
    sendOtp: 'OTP পঠাওক',
    sending: 'পঠোৱা হৈছে…',
    enterOtpPlaceholder: 'OTP লিখক',
    verifying: 'পৰীক্ষা কৰি আছে…',
    verify: 'পৰীক্ষা কৰক',
    newPhonePlaceholder: 'নতুন মোবাইল নম্বৰ',
    sendOtpNew: 'নতুন নম্বৰলৈ OTP পঠাওক',
    newOtpPlaceholder: 'নতুন নম্বৰৰ পৰা OTP',
    reasonPlaceholder: 'কাৰণ (ঐচ্ছিক)',
    submitting: 'দাখিল কৰি আছে…',
    verifyNew: 'নতুন নম্বৰ পৰীক্ষা কৰক',
    successLine1: 'আপোনাৰ অনুৰোধ দাখিল হ’ল। নম্বৰ আপডেট হোৱাৰ আগতে দুজন এডমিনৰ অনুমোদন প্ৰয়োজন।',
    successLine2: 'অনুমোদনৰ পিছত আপুনি WhatsApp, SMS আৰু email সতৰ্কবাণী পাব।',
    close: 'বন্ধ কৰক',
    pendingRequestError: 'আপোনাৰ এটা অনুৰোধ ইতিমধ্যে বাকী আছে। এডমিন অনুমোদনৰ বাবে অপেক্ষা কৰক।',
    couldNotSendOtp: 'OTP পঠাব পৰা নগ’ল',
    enterSixDigitOtp: '৬ অংকৰ OTP লিখক',
    verificationFailed: 'পৰীক্ষা বিফল',
    invalidMobile: 'বৈধ ১০ অংকৰ মোবাইল নম্বৰ লিখক',
  },
  ur: {
    step1Label: 'مرحلہ ۱ از ۲ — موجودہ نمبر کی تصدیق',
    step1Hint: 'آپ کے رجسٹرڈ موبائل نمبر پر OTP بھیجا جائے گا',
    step2Label: 'مرحلہ ۲ از ۲ — نیا نمبر',
    step2Hint: 'اپنا نیا ۱۰ ہندسوں والا موبائل نمبر درج کریں',
    sendOtp: 'OTP بھیجیں',
    sending: 'بھیجا جا رہا ہے…',
    enterOtpPlaceholder: 'OTP درج کریں',
    verifying: 'تصدیق ہو رہی ہے…',
    verify: 'تصدیق کریں',
    newPhonePlaceholder: 'نیا موبائل نمبر',
    sendOtpNew: 'نئے نمبر پر OTP بھیجیں',
    newOtpPlaceholder: 'نئے نمبر سے OTP',
    reasonPlaceholder: 'وجہ (اختیاری)',
    submitting: 'جمع کیا جا رہا ہے…',
    verifyNew: 'نیا نمبر تصدیق کریں',
    successLine1: 'آپ کی درخواست جمع ہو گئی ہے۔ نمبر اپ ڈیٹ ہونے سے پہلے دو ایڈمن کی منظوری ضروری ہے۔',
    successLine2: 'منظوری کے بعد آپ کو WhatsApp، SMS اور email الرٹ ملے گا۔',
    close: 'بند کریں',
    pendingRequestError: 'آپ کی ایک درخواست پہلے سے زیرِ التوا ہے۔ براہِ کرم ایڈمن کی منظوری کا انتظار کریں۔',
    couldNotSendOtp: 'OTP نہیں بھیجا جا سکا',
    enterSixDigitOtp: '۶ ہندسوں والا OTP درج کریں',
    verificationFailed: 'تصدیق ناکام',
    invalidMobile: 'درست ۱۰ ہندسوں والا موبائل نمبر درج کریں',
  },
  ne: {
    step1Label: 'चरण १ मध्ये २ — हालको नम्बर प्रमाणित गर्नुहोस्',
    step1Hint: 'तपाईंको दर्ता भएको मोबाइल नम्बरमा OTP पठाइनेछ',
    step2Label: 'चरण २ मध्ये २ — नयाँ नम्बर',
    step2Hint: 'आफ्नो नयाँ १० अङ्कको मोबाइल नम्बर प्रविष्ट गर्नुहोस्',
    sendOtp: 'OTP पठाउनुहोस्',
    sending: 'पठाइँदै छ…',
    enterOtpPlaceholder: 'OTP प्रविष्ट गर्नुहोस्',
    verifying: 'प्रमाणित गरिँदै छ…',
    verify: 'प्रमाणित गर्नुहोस्',
    newPhonePlaceholder: 'नयाँ मोबाइल नम्बर',
    sendOtpNew: 'नयाँ नम्बरमा OTP पठाउनुहोस्',
    newOtpPlaceholder: 'नयाँ नम्बरबाट OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'पेश गरिँदै छ…',
    verifyNew: 'नयाँ नम्बर प्रमाणित गर्नुहोस्',
    successLine1: 'तपाईंको अनुरोध पेश भयो। नम्बर अपडेट हुनु अघि दुई एडमिनको स्वीकृति आवश्यक छ।',
    successLine2: 'स्वीकृतिपछि तपाईंले WhatsApp, SMS र email सूचना पाउनुहुनेछ।',
    close: 'बन्द गर्नुहोस्',
    pendingRequestError: 'तपाईंको एउटा अनुरोध पहिले नै विचाराधीन छ। कृपया एडमिन स्वीकृतिको प्रतीक्षा गर्नुहोस्।',
    couldNotSendOtp: 'OTP पठाउन सकिएन',
    enterSixDigitOtp: '६ अङ्कको OTP प्रविष्ट गर्नुहोस्',
    verificationFailed: 'प्रमाणीकरण असफल',
    invalidMobile: 'मान्य १० अङ्कको मोबाइल नम्बर प्रविष्ट गर्नुहोस्',
  },
  kok: {
    step1Label: 'पायरी १ पैकी २ — सध्याचो नंबर पडताळा',
    step1Hint: 'तुमच्या नोंदणीकृत मोबाईल नंबराचेर OTP धाडटलो',
    step2Label: 'पायरी २ पैकी २ — नवो नंबर',
    step2Hint: 'तुमचो नवो १० अंकी मोबाईल नंबर घाला',
    sendOtp: 'OTP धाडा',
    sending: 'धाडटा…',
    enterOtpPlaceholder: 'OTP घाला',
    verifying: 'पडताळटा…',
    verify: 'पडताळा',
    newPhonePlaceholder: 'नवो मोबाईल नंबर',
    sendOtpNew: 'नव्या नंबराचेर OTP धाडा',
    newOtpPlaceholder: 'नव्या नंबरावेलो OTP',
    reasonPlaceholder: 'कारण (ऐच्छिक)',
    submitting: 'सबमिट करता…',
    verifyNew: 'नवो नंबर पडताळा',
    successLine1: 'तुमची विनंती सबमिट जाली. नंबर अपडेट जायच्या आदीं दोन अॅडमिनांची मंजुरी जाय.',
    successLine2: 'मंजुरीनंतर तुमकां WhatsApp, SMS आनी email सूचना मेळटली.',
    close: 'बंद करा',
    pendingRequestError: 'तुमची एक विनंती आदींच प्रलंबित आसा. अॅडमिन मंजुरीची वाट पळया.',
    couldNotSendOtp: 'OTP धाडप शक्य जाले ना',
    enterSixDigitOtp: '६ अंकी OTP घाला',
    verificationFailed: 'पडताळणी अयशस्वी',
    invalidMobile: 'वैध १० अंकी मोबाईल नंबर घाला',
  },
  sat: {
    step1Label: 'ᱥᱚᱛᱚᱨ ᱑ ᱠᱷᱚᱱ ᱒ — ᱱᱤᱛᱚᱜ ᱱᱚᱢᱵᱚᱨ ᱯᱚᱨᱤᱠᱷᱚᱱ ᱢᱮ',
    step1Hint: 'ᱟᱯᱱᱟᱨ ᱧᱩᱛᱩᱢ ᱢᱚᱵᱟᱭᱤᱞ ᱱᱚᱢᱵᱚᱨ ᱨᱮ OTP ᱵᱷᱮᱡᱟᱭᱮᱭᱟ',
    step2Label: 'ᱥᱚᱛᱚᱨ ᱒ ᱠᱷᱚᱱ ᱒ — ᱱᱟᱣᱟ ᱱᱚᱢᱵᱚᱨ',
    step2Hint: 'ᱟᱯᱱᱟᱨ ᱱᱟᱣᱟ ᱑᱐ ᱮᱞ ᱢᱚᱵᱟᱭᱤᱞ ᱱᱚᱢᱵᱚᱨ ᱴᱟᱭᱚᱢ ᱢᱮ',
    sendOtp: 'OTP ᱵᱷᱮᱡᱟᱭ ᱢᱮ',
    sending: 'ᱵᱷᱮᱡᱟᱭ ᱦᱩᱭᱩᱜ ᱠᱟᱱᱟ…',
    enterOtpPlaceholder: 'OTP ᱴᱟᱭᱚᱢ ᱢᱮ',
    verifying: 'ᱯᱚᱨᱤᱠᱷᱚᱱ ᱦᱩᱭᱩᱜ ᱠᱟᱱᱟ…',
    verify: 'ᱯᱚᱨᱤᱠᱷᱚᱱ ᱢᱮ',
    newPhonePlaceholder: 'ᱱᱟᱣᱟ ᱢᱚᱵᱟᱭᱤᱞ ᱱᱚᱢᱵᱚᱨ',
    sendOtpNew: 'ᱱᱟᱣᱟ ᱱᱚᱢᱵᱚᱨ ᱨᱮ OTP ᱵᱷᱮᱡᱟᱭ ᱢᱮ',
    newOtpPlaceholder: 'ᱱᱟᱣᱟ ᱱᱚᱢᱵᱚᱨ ᱠᱷᱚᱱ OTP',
    reasonPlaceholder: 'ᱠᱟᱨᱚᱱ (ᱵᱟᱝ ᱡᱚᱨᱩᱨᱤ)',
    submitting: 'ᱡᱚᱢᱟ ᱦᱩᱭᱩᱜ ᱠᱟᱱᱟ…',
    verifyNew: 'ᱱᱟᱣᱟ ᱱᱚᱢᱵᱚᱨ ᱯᱚᱨᱤᱠᱷᱚᱱ ᱢᱮ',
    successLine1: 'ᱟᱯᱱᱟᱨ ᱵᱤᱱᱚᱛᱤ ᱡᱚᱢᱟ ᱦᱩᱭ ᱮᱱᱟ। ᱱᱚᱢᱵᱚᱨ ᱟᱯᱰᱮᱴ ᱦᱩᱭᱩᱜ ᱢᱮᱦᱤᱱ ᱫᱚ ᱵᱟᱨ ᱮᱰᱢᱤᱱ ᱨᱮᱭᱟᱜ ᱢᱚᱱᱡᱩᱨᱤ ᱞᱟᱹᱜᱤᱫ ᱠᱟᱱᱟ।',
    successLine2: 'ᱢᱚᱱᱡᱩᱨᱤ ᱛᱟᱭᱚᱢ ᱠᱷᱚᱱ ᱟᱯᱱᱟᱨ WhatsApp, SMS ᱟᱨ email ᱥᱩᱪᱱᱟ ᱢᱮᱭᱟᱢᱟ।',
    close: 'ᱵᱚᱸᱫ ᱢᱮ',
    pendingRequestError: 'ᱟᱯᱱᱟᱨ ᱢᱤᱫ ᱵᱤᱱᱚᱛᱤ ᱢᱟᱲᱟᱝ ᱠᱷᱟᱱ ᱵᱟᱠᱤ ᱠᱟᱱᱟ। ᱮᱰᱢᱤᱱ ᱢᱚᱱᱡᱩᱨᱤ ᱞᱟᱹᱜᱤᱫ ᱚᱛᱚᱢ ᱢᱮ।',
    couldNotSendOtp: 'OTP ᱵᱷᱮᱡᱟᱭ ᱵᱟᱝ ᱦᱩᱭ ᱞᱮᱱᱟ',
    enterSixDigitOtp: '᱖ ᱮᱞ OTP ᱴᱟᱭᱚᱢ ᱢᱮ',
    verificationFailed: 'ᱯᱚᱨᱤᱠᱷᱚᱱ ᱵᱤᱠᱞ',
    invalidMobile: 'ᱴᱷᱤᱠ ᱵᱟᱲᱟᱝ ᱑᱐ ᱮᱞ ᱢᱚᱵᱟᱭᱤᱞ ᱱᱚᱢᱵᱚᱨ ᱴᱟᱭᱚᱢ ᱢᱮ',
  },
  mai: {
    step1Label: 'चरण १ मे २ — वर्तमान नंबर सत्यापित करू',
    step1Hint: 'अहाँक पंजीकृत मोबाइल नंबर पर OTP भेजल जायत',
    step2Label: 'चरण २ मे २ — नव नंबर',
    step2Hint: 'अपन नव १० अंकक मोबाइल नंबर दर्ज करू',
    sendOtp: 'OTP भेजू',
    sending: 'भेजल जा रहल अछि…',
    enterOtpPlaceholder: 'OTP दर्ज करू',
    verifying: 'सत्यापित हो रहल अछि…',
    verify: 'सत्यापित करू',
    newPhonePlaceholder: 'नव मोबाइल नंबर',
    sendOtpNew: 'नव नंबर पर OTP भेजू',
    newOtpPlaceholder: 'नव नंबर सँ OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा हो रहल अछि…',
    verifyNew: 'नव नंबर सत्यापित करू',
    successLine1: 'अहाँक अनुरोध जमा भेल अछि। नंबर अपडेट होबा सँ पहिने दू एडमिनक स्वीकृति आवश्यक अछि।',
    successLine2: 'स्वीकृति के बाद अहाँकेँ WhatsApp, SMS आओर email सूचना भेटत।',
    close: 'बंद करू',
    pendingRequestError: 'अहाँक एकटा अनुरोध पहिने सँ लंबित अछि। कृपया एडमिन स्वीकृति के प्रतीक्षा करू।',
    couldNotSendOtp: 'OTP नहि भेजल जा सकल',
    enterSixDigitOtp: '६ अंकक OTP दर्ज करू',
    verificationFailed: 'सत्यापन असफल',
    invalidMobile: 'वैध १० अंकक मोबाइल नंबर दर्ज करू',
  },
  doi: {
    step1Label: 'कदम १ च २ — मौजूदा नंबर पुष्टि करो',
    step1Hint: 'तुहाडे पंजीकृत मोबाइल नंबर पर OTP भेजा जावेगा',
    step2Label: 'कदम २ च २ — नया नंबर',
    step2Hint: 'अपना नया १० अंकी मोबाइल नंबर दर्ज करो',
    sendOtp: 'OTP भेजो',
    sending: 'भेजा जा रहा ऐ…',
    enterOtpPlaceholder: 'OTP दर्ज करो',
    verifying: 'पुष्टि हो रही ऐ…',
    verify: 'पुष्टि करो',
    newPhonePlaceholder: 'नया मोबाइल नंबर',
    sendOtpNew: 'नए नंबर पर OTP भेजो',
    newOtpPlaceholder: 'नए नंबर तों OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा हो रहा ऐ…',
    verifyNew: 'नया नंबर पुष्टि करो',
    successLine1: 'तुहाडी बिनती जमा हो गई ऐ। नंबर अपडेट होने तों पहिलें दो एडमिन दी मंजूरी चाहिदी ऐ।',
    successLine2: 'मंजूरी दे बाद तुहानूं WhatsApp, SMS ते email सूचना मिलेगी।',
    close: 'बंद करो',
    pendingRequestError: 'तुहाडी इक बिनती पहिलें ही लंबित ऐ। कृपया एडमिन मंजूरी दी प्रतीक्षा करो।',
    couldNotSendOtp: 'OTP नहीं भेजा जा सकया',
    enterSixDigitOtp: '६ अंकी OTP दर्ज करो',
    verificationFailed: 'पुष्टि असफल',
    invalidMobile: 'वैध १० अंकी मोबाइल नंबर दर्ज करो',
  },
  bho: {
    step1Label: 'चरण १ में २ — मौजूदा नंबर सत्यापित करीं',
    step1Hint: 'रउआ के पंजीकृत मोबाइल नंबर पर OTP भेजल जाई',
    step2Label: 'चरण २ में २ — नया नंबर',
    step2Hint: 'अपना नया १० अंकी मोबाइल नंबर भरीं',
    sendOtp: 'OTP भेजीं',
    sending: 'भेजल जा रहल बा…',
    enterOtpPlaceholder: 'OTP भरीं',
    verifying: 'सत्यापित हो रहल बा…',
    verify: 'सत्यापित करीं',
    newPhonePlaceholder: 'नया मोबाइल नंबर',
    sendOtpNew: 'नया नंबर पर OTP भेजीं',
    newOtpPlaceholder: 'नया नंबर से OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा हो रहल बा…',
    verifyNew: 'नया नंबर सत्यापित करीं',
    successLine1: 'रउआ के अनुरोध जमा हो गइल बा। नंबर अपडेट होखे से पहिले दू गो एडमिन के मंजूरी जरूरी बा।',
    successLine2: 'मंजूरी के बाद रउआ के WhatsApp, SMS आ email सूचना मिली।',
    close: 'बंद करीं',
    pendingRequestError: 'रउआ के एगो अनुरोध पहिले से लंबित बा। कृपया एडमिन मंजूरी के इंतजार करीं।',
    couldNotSendOtp: 'OTP ना भेजल जा सकल',
    enterSixDigitOtp: '६ अंकी OTP भरीं',
    verificationFailed: 'सत्यापन असफल',
    invalidMobile: 'सही १० अंकी मोबाइल नंबर भरीं',
  },
  raj: {
    step1Label: 'चरण १ मांहैं २ — मौजूदा नंबर सत्यापित करो',
    step1Hint: 'आपरो पंजीकृत मोबाइल नंबर पर OTP भेज्यो जावेला',
    step2Label: 'चरण २ मांहैं २ — नयो नंबर',
    step2Hint: 'आपरो नयो १० अंकड़ो मोबाइल नंबर भरो',
    sendOtp: 'OTP भेजो',
    sending: 'भेज्यो जा रियो है…',
    enterOtpPlaceholder: 'OTP भरो',
    verifying: 'सत्यापित हो रियो है…',
    verify: 'सत्यापित करो',
    newPhonePlaceholder: 'नयो मोबाइल नंबर',
    sendOtpNew: 'नये नंबर पर OTP भेजो',
    newOtpPlaceholder: 'नये नंबर सूं OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा हो रियो है…',
    verifyNew: 'नयो नंबर सत्यापित करो',
    successLine1: 'आपरी अरज जमा हो गई है। नंबर अपडेट होवे सूं पहलां दो एडमिन री मंजूरी जरूरी है।',
    successLine2: 'मंजूरी पाछै आपने WhatsApp, SMS अर email सूचना मिलेली।',
    close: 'बंद करो',
    pendingRequestError: 'आपरी एक अरज पहलां सूं लंबित है। कृपया एडमिन मंजूरी री प्रतीक्षा करो।',
    couldNotSendOtp: 'OTP नहीं भेज्यो जा सक्यो',
    enterSixDigitOtp: '६ अंकड़ो OTP भरो',
    verificationFailed: 'सत्यापन असफल',
    invalidMobile: 'सही १० अंकड़ो मोबाइल नंबर भरो',
  },
  cg: {
    step1Label: 'चरण १ मं २ — मौजूदा नंबर जाँच करव',
    step1Hint: 'आपमन के पंजीकृत मोबाइल नंबर म OTP भेजा जाही',
    step2Label: 'चरण २ मं २ — नवा नंबर',
    step2Hint: 'अपन नवा १० अंक के मोबाइल नंबर भरव',
    sendOtp: 'OTP भेजव',
    sending: 'भेजा जा थे…',
    enterOtpPlaceholder: 'OTP भरव',
    verifying: 'जाँच हो थे…',
    verify: 'जाँच करव',
    newPhonePlaceholder: 'नवा मोबाइल नंबर',
    sendOtpNew: 'नवा नंबर म OTP भेजव',
    newOtpPlaceholder: 'नवा नंबर से OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा हो थे…',
    verifyNew: 'नवा नंबर जाँच करव',
    successLine1: 'आपमन के निवेदन जमा हो गे। नंबर अपडेट होए से पहिली दू एडमिन के मंजूरी जरूरी हे।',
    successLine2: 'मंजूरी के बाद आपमन ल WhatsApp, SMS अउ email सूचना मिलही।',
    close: 'बंद करव',
    pendingRequestError: 'आपमन के एक निवेदन पहिली से लंबित हे। कृपया एडमिन मंजूरी के इंतजार करव।',
    couldNotSendOtp: 'OTP नहीं भेजा जा सके',
    enterSixDigitOtp: '६ अंक के OTP भरव',
    verificationFailed: 'जाँच असफल',
    invalidMobile: 'सही १० अंक के मोबाइल नंबर भरव',
  },
  har: {
    step1Label: 'चरण १ मं २ — मौजूदा नंबर जांच करो',
    step1Hint: 'थारे पंजीकृत मोबाइल नंबर पर OTP भेज्या जावैगा',
    step2Label: 'चरण २ मं २ — नया नंबर',
    step2Hint: 'अपना नया १० अंक का मोबाइल नंबर भरो',
    sendOtp: 'OTP भेजो',
    sending: 'भेज्या जा रह्या सै…',
    enterOtpPlaceholder: 'OTP भरो',
    verifying: 'जांच हो रही सै…',
    verify: 'जांच करो',
    newPhonePlaceholder: 'नया मोबाइल नंबर',
    sendOtpNew: 'नए नंबर पर OTP भेजो',
    newOtpPlaceholder: 'नए नंबर तै OTP',
    reasonPlaceholder: 'कारण (वैकल्पिक)',
    submitting: 'जमा हो रह्या सै…',
    verifyNew: 'नया नंबर जांच करो',
    successLine1: 'थारी अरज जमा हो गई सै। नंबर अपडेट होण तै पहलै दो एडमिन की मंजूरी जरूरी सै।',
    successLine2: 'मंजूरी के बाद थारै WhatsApp, SMS अर email सूचना मिलैगी।',
    close: 'बंद करो',
    pendingRequestError: 'थारी एक अरज पहलै तै लंबित सै। कृपया एडमिन मंजूरी का इंतजार करो।',
    couldNotSendOtp: 'OTP नहीं भेज्या जा सका',
    enterSixDigitOtp: '६ अंक का OTP भरो',
    verificationFailed: 'जांच असफल',
    invalidMobile: 'सही १० अंक का मोबाइल नंबर भरो',
  },
};

function labelsForLang(lang) {
  return PHONE_CHANGE_LABELS[lang] || PHONE_CHANGE_LABELS.en;
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.88)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 999,
    padding: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  modal: {
    background: 'linear-gradient(135deg, #0f2040, #0a1628)',
    border: '1px solid rgba(201,168,76,0.4)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 30px 80px rgba(0,0,0,0.8)',
    width: 'min(430px, 95vw)',
    maxHeight: 'min(90dvh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 32px))',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    margin: 'auto 0',
    boxSizing: 'border-box',
  },
  title: { color: '#c9a84c', fontSize: '16px', margin: '0 0 16px', textAlign: 'center' },
  text: { color: '#8896a8', fontSize: '13px', margin: '0 0 12px', lineHeight: 1.5 },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: '8px',
    color: '#e8e0d0',
    fontSize: '13px',
    marginBottom: '4px',
  },
  error: { color: '#e94560', fontSize: '11px', margin: '0 0 10px', minHeight: '14px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '8px' },
  cancelBtn: {
    flex: 1,
    padding: '11px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#8896a8',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  confirmBtn: {
    flex: 1,
    padding: '11px',
    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
    color: '#0a1628',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '700',
  },
  stepLabel: {
    color: 'rgba(201,168,76,0.7)',
    fontSize: '10px',
    letterSpacing: '1px',
    margin: '0 0 12px',
    textAlign: 'center',
  },
};

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export default function PhoneChangeModal({ isOpen, onClose, userRole, authToken, onPhoneChanged }) {
  const { lang, t } = useLanguage();
  const L = useMemo(() => labelsForLang(lang), [lang]);
  const [step, setStep] = useState(1);
  const [oldOtpSent, setOldOtpSent] = useState(false);
  const [oldOtp, setOldOtp] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newOtpSent, setNewOtpSent] = useState(false);
  const [newOtp, setNewOtp] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [oldOtpError, setOldOtpError] = useState('');
  const [newPhoneError, setNewPhoneError] = useState('');
  const [newOtpError, setNewOtpError] = useState('');
  const [generalError, setGeneralError] = useState('');

  const resetState = useCallback(() => {
    setStep(1);
    setOldOtpSent(false);
    setOldOtp('');
    setNewPhone('');
    setNewOtpSent(false);
    setNewOtp('');
    setReason('');
    setLoading(false);
    setOldOtpError('');
    setNewPhoneError('');
    setNewOtpError('');
    setGeneralError('');
  }, []);

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen, resetState]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const apiFetch = async (path, body) => {
    const res = await fetch(`${API_BASE_URL}/api/phone-change${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 409) {
      const msg = await parseApiError(res);
      if (msg.toLowerCase().includes('pending')) {
        throw new Error(L.pendingRequestError);
      }
      throw new Error(msg);
    }
    if (!res.ok) {
      throw new Error(await parseApiError(res));
    }
    return res.json();
  };

  const handleSendOldOtp = async () => {
    setGeneralError('');
    setOldOtpError('');
    setLoading(true);
    try {
      await apiFetch('/send-otp-old');
      setOldOtpSent(true);
    } catch (err) {
      setGeneralError(err.message || L.couldNotSendOtp);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOld = async () => {
    setOldOtpError('');
    setGeneralError('');
    if (!/^\d{6}$/.test(oldOtp.trim())) {
      setOldOtpError(L.enterSixDigitOtp);
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/verify-old', { otp: oldOtp.trim() });
      setStep(2);
    } catch (err) {
      setOldOtpError(err.message || L.verificationFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNewOtp = async () => {
    setNewPhoneError('');
    setGeneralError('');
    const phoneClean = newPhone.replace(/\D/g, '');
    if (!/^[6-9]\d{9}$/.test(phoneClean)) {
      setNewPhoneError(L.invalidMobile);
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/send-otp-new', { new_phone: phoneClean });
      setNewOtpSent(true);
    } catch (err) {
      setNewPhoneError(err.message || L.couldNotSendOtp);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNewAndSubmit = async () => {
    setNewOtpError('');
    setGeneralError('');
    if (!/^\d{6}$/.test(newOtp.trim())) {
      setNewOtpError(L.enterSixDigitOtp);
      return;
    }
    setLoading(true);
    try {
      const payload = { otp: newOtp.trim() };
      const trimmedReason = reason.trim();
      if (trimmedReason) payload.reason = trimmedReason;
      await apiFetch('/verify-new-and-submit', payload);
      const phoneClean = newPhone.replace(/\D/g, '');
      if (onPhoneChanged && phoneClean) onPhoneChanged(phoneClean);
      setStep(3);
    } catch (err) {
      setNewOtpError(err.message || L.verificationFailed);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={handleClose} role="presentation">
      <div
        style={modalStyles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="phone-change-modal-title"
      >
        <h3 id="phone-change-modal-title" style={modalStyles.title}>{t('changeMobileNumber')}</h3>

        {step === 1 && (
          <>
            <p style={modalStyles.stepLabel}>{L.step1Label}</p>
            <p style={modalStyles.text}>{L.step1Hint}</p>
            {generalError && <p style={modalStyles.error}>{generalError}</p>}
            <button
              type="button"
              style={{ ...modalStyles.confirmBtn, width: '100%', marginBottom: '14px', opacity: loading ? 0.6 : 1 }}
              disabled={loading}
              onClick={handleSendOldOtp}
            >
              {loading && !oldOtpSent ? L.sending : L.sendOtp}
            </button>
            {oldOtpSent && (
              <>
                <input
                  style={modalStyles.input}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={L.enterOtpPlaceholder}
                  value={oldOtp}
                  onChange={(e) => setOldOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <p style={modalStyles.error}>{oldOtpError}</p>
                <button
                  type="button"
                  style={{ ...modalStyles.confirmBtn, width: '100%', opacity: loading ? 0.6 : 1 }}
                  disabled={loading}
                  onClick={handleVerifyOld}
                >
                  {loading ? L.verifying : L.verify}
                </button>
              </>
            )}
            <div style={modalStyles.btnRow}>
              <button type="button" style={modalStyles.cancelBtn} onClick={handleClose} disabled={loading}>
                {t('cancelText')}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p style={modalStyles.stepLabel}>{L.step2Label}</p>
            <p style={modalStyles.text}>{L.step2Hint}</p>
            {generalError && <p style={modalStyles.error}>{generalError}</p>}
            <input
              style={modalStyles.input}
              inputMode="numeric"
              maxLength={10}
              placeholder={L.newPhonePlaceholder}
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            />
            <p style={modalStyles.error}>{newPhoneError}</p>
            <button
              type="button"
              style={{ ...modalStyles.confirmBtn, width: '100%', marginBottom: '14px', opacity: loading ? 0.6 : 1 }}
              disabled={loading}
              onClick={handleSendNewOtp}
            >
              {loading && !newOtpSent ? L.sending : L.sendOtpNew}
            </button>
            {newOtpSent && (
              <>
                <input
                  style={modalStyles.input}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={L.newOtpPlaceholder}
                  value={newOtp}
                  onChange={(e) => setNewOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <p style={modalStyles.error}>{newOtpError}</p>
                <input
                  style={modalStyles.input}
                  placeholder={L.reasonPlaceholder}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                />
                <button
                  type="button"
                  style={{ ...modalStyles.confirmBtn, width: '100%', marginBottom: '8px', opacity: loading ? 0.6 : 1 }}
                  disabled={loading}
                  onClick={handleVerifyNewAndSubmit}
                >
                  {loading ? L.submitting : L.verifyNew}
                </button>
              </>
            )}
            <div style={modalStyles.btnRow}>
              <button type="button" style={modalStyles.cancelBtn} onClick={handleClose} disabled={loading}>
                {t('cancelText')}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p style={modalStyles.text}>
              {L.successLine1}
            </p>
            <p style={modalStyles.text}>
              {L.successLine2}
            </p>
            <button type="button" style={{ ...modalStyles.confirmBtn, width: '100%' }} onClick={handleClose}>
              {L.close}
            </button>
          </>
        )}

      </div>
    </div>
  );
}
